import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { get, uniqBy, find } from 'lodash';
import Modal, {
  ModalBody,
  ModalContent,
  ModalHeader,
} from '../../../../components/modal/Modal';
import { useModelInfo } from '../../../../common/ModelInfoContext';
import { useTranslate } from 'core/translate';
import fetchCapabilities from '../../../../api/fetchCapabilities';
import fetchModelMatrix from '../../../../api/fetchModelMatrix';
import Compare from '../../../../common/compare/Compare';
import { combineWithEngine } from '../../../../components/compareManager/utils';
import LoadingSpecsCompare from '../../../../common/loading/LoadingSpecsCompare';
import useCompareModalScrolling from '../../../../components/compareManager/useCompareModalScrolling';

const analyticsKey = 'compare capabilities';

const toColumn = ({ bodyTypeId, driveType, engine }, engineRowData) => {
  const id = `${bodyTypeId}_${driveType}_${engine}`;
  return get(engineRowData, id);
};

const toColumns = (combinedOptions, engineRowData) => {
  return combinedOptions.map((option) => {
    return {
      data: option,
      groups: toColumn(option, engineRowData),
    };
  });
};

const CapabilitiesCompareInner = ({
  config,
  capabilities,
  modelMatrix,
  featureSpecs,
  trimFiltersSelections,
  filteredEngines,
  onSelect,
  onClose,
  isScrolled,
}) => {
  const options = combineWithEngine(
    config,
    featureSpecs.options
  )(modelMatrix)
    .map((option) => {
      const id = +option.bodyTypeId;
      const bodyType = find(config.bodyTypes, { id });

      return {
        ...option,
        rearWheelType: get(bodyType, 'rearWheelType', ''),
        hasWheelBase: get(bodyType, 'wheelBaseType', ''),
      };
    })
    .filter((opt) => find(filteredEngines, { id: opt.engine }));

  const { engineRowData, groups } = capabilities;

  const getSelectedIndex = (options) => {
    if (trimFiltersSelections?.engine) {
      return options.findIndex(
        ({ engine }) => trimFiltersSelections?.engine === engine
      );
    }
    return 0;
  };

  let uniqueOptions = uniqBy(options, (x) => `${x.engine}`).map((opt) => {
    return {
      ...opt,
      disabled: get(find(filteredEngines, { id: opt.engine }), 'disabled'),
    };
  });

  const selectedEngineIndex = getSelectedIndex(uniqueOptions);

  const columns = toColumns(uniqueOptions, engineRowData);

  const translate = useTranslate();

  const headers = uniqueOptions.map(
    ({ engineDescription, trimName, engine }) => {
      return {
        primaryText: engineDescription,
        trimKey: trimName,
        selected: trimFiltersSelections?.engine === engine,
      };
    }
  );

  const filterText = translate('capability.compare.filtered.v3', {
    number: columns && columns.length,
  });

  const handleSelect = (opt) => {
    const engineObj = { id: opt?.data?.engine };
    onSelect(engineObj);
    onClose();
  };

  return (
    <Compare
      headers={headers}
      columns={columns}
      compareGroups={groups}
      config={config}
      isScrolled={isScrolled}
      displaySort={false}
      displayFilterCount={false}
      driveTypeSectionEl={false}
      displayInTableFilter={false}
      itemDescription={translate('component.common.select.engine')}
      filterText={columns && columns.length && filterText}
      hideMSRPDisclosure={true}
      isFeatures={true}
      analyticsKey={analyticsKey}
      onSelect={handleSelect}
      selectedIndex={selectedEngineIndex}
    />
  );
};

const CapabilitiesCompareModal = ({
  config,
  featureSpecs,
  onClose,
  filteredEngines,
  trimFiltersSelections,
  onSelect,
}) => {
  const translate = useTranslate();
  const modelInfo = useModelInfo();
  const [contentRef, setContentRef] = useState(null);

  const [isLoading, setIsLoading] = useState(true);
  const [modelMatrix, setModelMatrix] = useState();
  const [capabilities, setCapabilities] = useState();

  const header = translate('capabilities.compare.title.v3', {
    model: modelInfo.title,
  });

  const [scrolling] = useCompareModalScrolling(
    contentRef && contentRef.querySelector('.capabilities-compare__modal-body')
  );

  const fetchData = (abortController) => {
    Promise.all([
      fetchCapabilities(modelInfo),
      fetchModelMatrix({ ...modelInfo, config }),
    ]).then(([capabilities, modelMatrix]) => {
      if (!abortController || !abortController.signal.aborted) {
        setModelMatrix(modelMatrix);
        setCapabilities(capabilities);
        setIsLoading(false);
      }
    });
  };

  useEffect(() => {
    const abortController = new AbortController();
    fetchData(abortController);
    return () => abortController.abort();
  }, []);

  return (
    <Modal
      isOpen={true}
      contentLabel={header}
      onRequestClose={onClose}
      contentRefCallback={(node) => setContentRef(node)}>
      <ModalHeader text={header} onClose={onClose} />
      <ModalBody
        className="capabilities-compare__modal-body"
        innerClassName="compare__nav-padding compare__nav-padding--capabilities">
        <ModalContent>
          <>
            {isLoading ? (
              <LoadingSpecsCompare />
            ) : (
              <CapabilitiesCompareInner
                modelMatrix={modelMatrix}
                capabilities={capabilities}
                config={config}
                featureSpecs={featureSpecs}
                filteredEngines={filteredEngines}
                trimFiltersSelections={trimFiltersSelections}
                onSelect={onSelect}
                onClose={onClose}
                isScrolled={scrolling}
              />
            )}
          </>
        </ModalContent>
      </ModalBody>
    </Modal>
  );
};

const mapStateToProps = (state) => {
  const { config } = state;

  return {
    config,
  };
};

export default connect(mapStateToProps)(CapabilitiesCompareModal);
